import React, { useState, useRef } from "react";
import { Sparkles, Send } from "lucide-react";
import Seo from "../components/Seo";
import MessageList from "../components/MessageList";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { log } from "../lib/log";

const SUGGESTIONS = [
  "How do I fly business class to Tokyo on points?",
  "What's a sweet spot for Europe from New York?",
  "Which card points transfer to Aeroplan?",
  "Help me plan a trip to Sydney in October.",
];
const WELCOME = { id: "welcome", role: "assistant", text: "Hi! I'm your FindMyFlight assistant. Ask me anything about points, miles, sweet spots, or planning your next award trip." };
const FALLBACK = "Sorry, I ran into an issue reaching the assistant. Please try again in a moment.";

const newSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const msg = (role, text) => ({ id: `${role[0]}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, role, text });

function useChat() {
  const [messages, setMessages] = useState([WELCOME]);
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(newSessionId());

  const send = async (text) => {
    const clean = text.trim();
    if (!clean || loading) return;
    setMessages((m) => [...m, msg("user", clean)]);
    setLoading(true);
    try {
      const reply = await api.chat(sessionRef.current, clean);
      setMessages((m) => [...m, msg("assistant", reply)]);
    } catch (err) {
      log.error("Chat request failed", err);
      setMessages((m) => [...m, msg("assistant", FALLBACK)]);
    } finally {
      setLoading(false);
    }
  };
  return { messages, loading, send };
}

function Composer({ onSend, disabled }) {
  const [input, setInput] = useState("");
  const submit = () => { onSend(input); setInput(""); };
  return (
    <div className="mb-5 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
      <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Ask about points, sweet spots, or trip planning..." data-testid="chat-input" className="flex-1 bg-transparent px-3 text-sm outline-none" />
      <Button onClick={submit} disabled={disabled} aria-label="Send" data-testid="chat-send" className="h-10 w-10 bg-blue-600 p-0 hover:bg-blue-700"><Send className="h-4 w-4" /></Button>
    </div>
  );
}

export default function Chat() {
  const { messages, loading, send } = useChat();
  return (
    <main className="bg-slate-50">
      <Seo title="AI Assistant — plan your award trip | FindMyFlight" description="Chat with the FindMyFlight AI assistant to find sweet spots, plan trips, and answer any points and miles question." path="/chat" />
      <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-5">
        <div className="flex items-center gap-2 py-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white"><Sparkles className="h-4 w-4" /></span>
          <div>
            <h1 className="text-lg font-bold text-slate-900">AI Assistant</h1>
            <p className="text-xs text-slate-500">Your personal points & miles concierge</p>
          </div>
        </div>
        <MessageList messages={messages} loading={loading} />
        {messages.length <= 1 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600">{s}</button>
            ))}
          </div>
        )}
        <Composer onSend={send} disabled={loading} />
      </div>
    </main>
  );
}
